import { useState } from "react";
import { Plus, Pencil, Trash2, CloudSun } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { StatusBadge } from "./StatusBadge";
import { LogDialog } from "./LogDialog";
import { STATUSES, formatDate } from "../lib/api";
import { formatDateTime } from "./HistoryLog";

export const DailyLog = ({ logs, teams, jobs, onSave, onDelete, readOnly }) => {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [fDate, setFDate] = useState("");
  const [fTeam, setFTeam] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const teamName = (id) => teams.find((t) => t.id === id)?.name || "-";
  const job = (id) => jobs.find((j) => j.id === id);

  const rows = logs.filter((l) => (!fDate || l.date === fDate) && (fTeam === "all" || l.team_id === fTeam) && (fStatus === "all" || l.status === fStatus));
  const openNew = () => { setEditing(null); setOpen(true); };
  const save = async (f) => { await onSave(f, editing?.id); setOpen(false); };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <Input data-testid="daily-log-date-filter" type="date" value={fDate} onChange={(e) => setFDate(e.target.value)} className="w-44" />
        <Select value={fTeam} onValueChange={setFTeam}>
          <SelectTrigger data-testid="daily-log-team-filter" className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All teams</SelectItem>{teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={fStatus} onValueChange={setFStatus}>
          <SelectTrigger data-testid="daily-log-status-filter" className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
        </Select>
        {(fDate || fTeam !== "all" || fStatus !== "all") && (
          <Button variant="ghost" data-testid="daily-log-clear-filters" onClick={() => { setFDate(""); setFTeam("all"); setFStatus("all"); }}>Clear</Button>
        )}
        {!readOnly && <Button data-testid="daily-log-add-button" onClick={openNew} className="ml-auto bg-amber-500 text-black hover:bg-amber-400"><Plus className="mr-1 h-4 w-4" /> Add Daily Log</Button>}
      </div>

      <div className="panel overflow-x-auto">
        <table data-testid="daily-log-table" className="w-full min-w-[900px] text-sm">
          <thead>
            <tr className="border-b border-[color:var(--border)] text-left">
              {["Date", "Team", "Job", "Status", "Progress", "Weather", "Notes", ...(readOnly ? [] : ["Last Edited"]), ""].map((h) => <th key={h} className="label px-4 py-3 font-normal">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={readOnly ? 8 : 9} data-testid="daily-log-empty" className="px-4 py-10 text-center text-[color:var(--muted)]">{readOnly ? "No daily logs yet." : "No daily logs yet. Click \"Add Daily Log\" to record today's work."}</td></tr>}
            {rows.map((l) => (
              <tr key={l.id} data-testid={`daily-log-row-${l.id}`} className="border-b border-[color:var(--border)] last:border-0 hover:bg-white/[0.02]">
                <td className="whitespace-nowrap px-4 py-3 font-mono">{formatDate(l.date)}</td>
                <td className="px-4 py-3 font-display font-semibold">{teamName(l.team_id)}</td>
                <td className="px-4 py-3">{job(l.job_id) ? `${job(l.job_id).no}. ${job(l.job_id).name}` : "-"}</td>
                <td className="px-4 py-3"><StatusBadge status={l.status} /></td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 w-16 overflow-hidden rounded-full bg-[color:var(--border)]"><div className="h-full bg-amber-500" style={{ width: `${l.progress}%` }} /></div>
                    <span className="font-mono text-xs">{l.progress}%</span>
                  </div>
                </td>
                <td className="whitespace-nowrap px-4 py-3"><CloudSun className="mr-1 inline h-4 w-4 text-[color:var(--muted)]" />{l.weather}</td>
                <td className="max-w-xs px-4 py-3 text-[color:var(--muted)]">{l.notes || "-"}</td>
                {!readOnly && (
                  <td data-testid={`daily-log-edited-${l.id}`} className="whitespace-nowrap px-4 py-3" title={l.created_by ? `Created by ${l.created_by} · ${formatDateTime(l.created_at)}` : ""}>
                    {l.updated_by ? (
                      <>
                        <div className="font-display font-semibold">{l.updated_by}</div>
                        <div className="font-mono text-[11px] text-[color:var(--muted)]">{formatDateTime(l.updated_at || l.created_at)}</div>
                      </>
                    ) : <span className="font-mono text-xs text-[color:var(--muted)]">—</span>}
                  </td>
                )}
                <td className="whitespace-nowrap px-4 py-3 text-right">
                  {!readOnly && <>
                  <Button size="icon" variant="ghost" data-testid={`daily-log-edit-${l.id}`} onClick={() => { setEditing(l); setOpen(true); }}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" data-testid={`daily-log-delete-${l.id}`} onClick={() => onDelete(l.id)} className="text-red-400 hover:text-red-300"><Trash2 className="h-4 w-4" /></Button>
                  </>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <LogDialog open={open} onOpenChange={setOpen} initial={editing} teams={teams} jobs={jobs} onSave={save} />
    </div>
  );
};
